import React, { useMemo } from 'react';

import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';

import { AnyAri } from '@atlassian/ari/any-ari';
import type { PastMeetingOccurrence } from '@atlassian/search-client/past-meeting-occurrences/types';
import { ResourceType } from '@atlassian/search-client/third-party-graph-entity';

import { ProductKeys3P } from '../../../../../../common/constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../../../../common/constants/schemas/3p-config';
import type { SearchResultProps } from '../../../../../../common/types';
import { IconFor3P } from '../../../../../../common/ui/icon-for-3p';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { serve3PClickIngestionExperiment } from '../../../../../../common/utils/experiments';
import { isProductImpersonated } from '../../../../../../common/utils/is-product-impersonated';
import { get3PProductFromProviderIdOrIntegrationId } from '../../../../../../common/utils/search-result-utils';
import {
	getResultConnectorSource,
	isResultSmartLink,
} from '../../../../../../common/utils/third-party-utils';
import { useLogException } from '../../../../../../controllers/error-boundary/utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchGraphResult_AtlassianSearchPage$key } from './__generated__/searchGraphResult_AtlassianSearchPage.graphql';
import { SearchGraphBranch } from './search-graph-branch';
import { SearchGraphCalendar } from './search-graph-calendar';
import { SearchGraphCampaign } from './search-graph-campaign';
import { SearchGraphCase } from './search-graph-case';
import { SearchGraphComment } from './search-graph-comment';
import { SearchGraphCommit } from './search-graph-commit';
import { SearchGraphConfluenceDc } from './search-graph-confluence-dc';
import { SearchGraphConversation } from './search-graph-conversation';
import { SearchGraphCustomerContact } from './search-graph-customer-contact';
import { SearchGraphCustomerOrg } from './search-graph-customer-org';
import { SearchGraphDashboard } from './search-graph-dashboard';
import { SearchGraphDataTable } from './search-graph-data-table';
import { SearchGraphDeal } from './search-graph-deal';
import { SearchGraphDefault } from './search-graph-default';
import { SearchGraphDesign } from './search-graph-design';
import { SearchGraphDocument } from './search-graph-document';
import { SearchGraphJiraDc } from './search-graph-jira-dc';
import { SearchGraphMessage } from './search-graph-message';
import { SearchGraphPullRequest } from './search-graph-pull-request';
import { SearchGraphRepository } from './search-graph-repository';
import { SearchGraphVideo } from './search-graph-video';
import { SearchGraphWorkItem } from './search-graph-work-item';

type SearchGraphResultProps = SearchResultProps<searchGraphResult_AtlassianSearchPage$key> & {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	is3pGroupingEnabled?: boolean;
	pastMeetingOccurrences?: PastMeetingOccurrence[] | null;
};
/**
 * The naming here is a little confusing. SearchResultGraphDocument is the generic graph type, not the "Document" subtype of graph!
 */
export const SearchGraphResult = (props: SearchGraphResultProps): React.JSX.Element => {
	const logException = useLogException();
	const { node, position, thirdPartyConfigs } = props;
	// note that `SearchResultGraphDocument` in this fragment isn't a document type from graph, it's the base type!
	// TODO: QS-6952 optIn may not be working, entity.parent is always null
	const data = useFragment(
		graphql`
			fragment searchGraphResult_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(includeThirdPartyId: { type: "Boolean!", defaultValue: true }) {
				__typename
				id
				providerId
				connectorType
				integrationId
				subtype
				...searchGraphBranch_AtlassianSearchPage
				...searchGraphCalendar_AtlassianSearchPage
				...searchGraphComment_AtlassianSearchPage
				...searchGraphCommit_AtlassianSearchPage
				...searchGraphConfluenceDc_AtlassianSearchPage
				...searchGraphConversation_AtlassianSearchPage
				...searchGraphCampaign_AtlassianSearchPage
				...searchGraphCase_AtlassianSearchPage
				...searchGraphCustomerOrg_AtlassianSearchPage
				...searchGraphDeal_AtlassianSearchPage
				...searchGraphDefault_AtlassianSearchPage
				...searchGraphDesign_AtlassianSearchPage
				...searchGraphDocument_AtlassianSearchPage
					@arguments(includeThirdPartyId: $includeThirdPartyId)
				...searchGraphMessage_AtlassianSearchPage
				...searchGraphJiraDc_AtlassianSearchPage
				...searchGraphPullRequest_AtlassianSearchPage
				...searchGraphRepository_AtlassianSearchPage
				...searchGraphVideo_AtlassianSearchPage
				...searchGraphWorkItem_AtlassianSearchPage
				...searchGraphDashboard_AtlassianSearchPage
				...searchGraphDataTable_AtlassianSearchPage
				...searchGraphCustomerContact_AtlassianSearchPage
			}
		`,
		node,
	);

	let { id, providerId, integrationId, connectorType } = data;
	const subtype = data?.subtype || null;

	const { resourceType, resourceId } = AnyAri.parse(id);

	const product = get3PProductFromProviderIdOrIntegrationId({
		thirdPartyConfigs,
		providerId,
		integrationId,
		resourceType,
	});

	const metadata = useMemo(() => {
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: subtype,
			entityType: resourceType,
			contentId: resourceId,
			product: product,
			is3PSmartLink: isResultSmartLink({
				connectorType,
				resultAri: id,
				productKey: product,
				thirdPartyConfigs,
			}),
			connectorType: getResultConnectorSource({
				connectorType,
				resultNodeId: id,
				productKey: product,
				thirdPartyConfigs,
			}),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [subtype, resourceType, resourceId, product, data.id, connectorType, id, thirdPartyConfigs]);

	const is3PClickIngestionEnabled = serve3PClickIngestionExperiment(thirdPartyConfigs, {
		fireExperimentExposure: false,
	});
	const onResultClicked = useFireResultClicked({
		metadata,
		position,
		isImpersonated: isProductImpersonated(product, thirdPartyConfigs),
		is3PClickIngestionEnabled,
	});
	const onResultContextMenu = useFireResultCtxMenuClicked({
		metadata,
		position,
		isImpersonated: isProductImpersonated(product, thirdPartyConfigs),
		is3PClickIngestionEnabled,
	});
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const baseProps = {
		...props,
		node: data,
		product,
		onResultClicked,
		onResultContextMenu,
		onCopyLinkButtonClicked,
		onSummarizeInChatButtonClicked,
		onContextMenuCopyButtonClicked,
		onContextMenuButtonClicked,
		subtype,
		entityType: resourceType,
	};

	const icon = (
		<IconFor3P
			product={product}
			resourceType={resourceType}
			subtype={subtype}
			thirdPartyConfigs={thirdPartyConfigs}
		/>
	);

	try {
		if (!product) {
			throw new Error(
				`SearchGraphResult: missing product for integrationId=${integrationId} providerId=${providerId}`,
			);
		}

		const SearchGraphRenderer = getGraphRenderer(product, resourceType);
		if (!SearchGraphRenderer) {
			throw new Error(
				`SearchGraphResult: missing renderer for resourceType=${resourceType} product=${product}`,
			);
		}

		return <SearchGraphRenderer {...baseProps} product={product} icon={icon} />;
	} catch (e) {
		logException(e);
		return <SearchGraphDefault {...baseProps} product={product} icon={icon} />;
	}
};

const getGraphRenderer = (product: ProductKeys3P, resourceType: string) => {
	if (product === ProductKeys3P.JiraDC) {
		return SearchGraphJiraDc;
	}
	switch (resourceType) {
		case ResourceType.CAMPAIGN:
			return SearchGraphCampaign;
		case ResourceType.CASE:
			return SearchGraphCase;
		case ResourceType.BRANCH:
			return SearchGraphBranch;
		case ResourceType.CALENDAR_EVENT:
			return SearchGraphCalendar;
		case ResourceType.COMMENT:
			return SearchGraphComment;
		case ResourceType.COMMIT:
			return SearchGraphCommit;
		case ResourceType.CONVERSATION:
			return SearchGraphConversation;
		case ResourceType.CUSTOMER_CONTACT:
			return SearchGraphCustomerContact;
		case ResourceType.CUSTOMER_ORG:
			return SearchGraphCustomerOrg;
		case ResourceType.DEAL:
			return SearchGraphDeal;
		case ResourceType.DOCUMENT:
			if (product === ProductKeys3P.ConfluenceDC) {
				return SearchGraphConfluenceDc;
			} else {
				return SearchGraphDocument;
			}
		case ResourceType.DESIGN:
			return SearchGraphDesign;
		case ResourceType.MESSAGE:
			return SearchGraphMessage;
		case ResourceType.PULL_REQUEST:
			return SearchGraphPullRequest;
		case ResourceType.REPOSITORY:
			return SearchGraphRepository;
		case ResourceType.VIDEO:
			return SearchGraphVideo;
		case ResourceType.WORK_ITEM:
			return SearchGraphWorkItem;
		case ResourceType.DASHBOARD:
			return SearchGraphDashboard;
		case ResourceType.DATA_TABLE:
			return SearchGraphDataTable;
		case ResourceType.REMOTE_LINK:
			return SearchGraphDefault; // Remote link intentionally uses the default renderer
		default:
			return null;
	}
};

export const isSearchGraphResult = (
	node?: any,
): node is searchGraphResult_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultGraphDocument';
};
